/**
 * PickingHub — Wave 30 Chunk 11e.
 *
 * Standalone full-screen page reachable from the sidebar (Tools →
 * Picking). Shows every picking list across every project the user
 * has access to, with filters by status and search. Each row is a
 * one-tap link into the parent project's detail page (which holds the
 * actual Picking card with the inline expanders for items, history,
 * breakdown, and add-row).
 *
 * Design contract:
 *   • Full-screen view (no modals, no popovers).
 *   • Same view+edit shell — clicking a list opens its project page,
 *     where editing/picking/returning happens inline.
 *   • Mobile-clean at 360 px — cards stack, status pills wrap, and the
 *     filter bar is horizontally scrollable.
 */
import { useEffect, useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  PackageCheck, Search, AlertTriangle, Loader2, RefreshCw,
  Download, Building2, ChevronRight as ChevronRightIcon, Plus,
} from "lucide-react";
import api from "../lib/api";
import { toast } from "../components/ui/sonner";
import { displayPickingListName } from "../lib/pickingListName";
import NewPickingSlipModal from "../components/picking/NewPickingSlipModal";

// Iter 398f — Dirk's ask: derived phase bucket per list, driven by
// row-level totals (see backend `_derive_phase`). Manual `Closed`
// only, everything else auto. Ordered by priority: void > closed >
// returned > dispatched > picking > open. Colours mirror the
// underlying movement kinds so the whole system reads consistently.
// Iter 418.j (Dirk, 2026-02-28) — added `picking` (in-progress) and
// `void` phases so ops can filter to slips that have been started
// but not dispatched, and to see cancelled slips separately.
const PHASE_PILL = {
  open:       { c: "#A78BFA", l: "Open" },
  // Iter 418.bn (Dirk, 2026-02-28) — new `ready` bucket. Slip is
  // fully specced and awaiting the picker; no rows picked yet.
  ready:      { c: "#F97316", l: "Ready" },
  picking:    { c: "#22D3EE", l: "Picking" },
  dispatched: { c: "#38BDF8", l: "Dispatched" },
  returned:   { c: "#F59E0B", l: "Returned" },
  complete:   { c: "#10B981", l: "Complete" },
  closed:     { c: "#647087", l: "Closed" },
  void:       { c: "#EF4444", l: "Void" },
};

const PHASE_FILTERS = [
  { v: "open",       l: "Open" },
  { v: "ready",      l: "Ready" },
  { v: "picking",    l: "Picking" },
  { v: "dispatched", l: "Dispatched" },
  { v: "returned",   l: "Returned" },
  { v: "complete",   l: "Complete" },
  { v: "closed",     l: "Closed" },
  { v: "void",       l: "Void" },
  { v: "all",        l: "All" },
];

export default function PickingHub() {
  const [lists, setLists] = useState([]);
  const [projects, setProjects] = useState({});  // pid -> {id, title, short_id}
  // Iter 290zn — customers + jobcards map to (a) render proper headers
  // for lists with no project ("LOOSE · KROON TRUST" instead of NULL)
  // and (b) upgrade legacy "JC <short> materials" names on the fly.
  const [customers, setCustomers] = useState({});  // cid -> {id, ref, name}
  const [jobcards, setJobcards] = useState({});    // jid -> {id, title, short_id}
  // Iter 398f — resolve sales order doc numbers so the family-tree
  // chip row on each picking slip reads "KRO001 · JC2145 · SO-124"
  // just like the entity trail bar at the top of the app does.
  const [salesDocs, setSalesDocs] = useState({});  // sd_id -> {id, document_number, category}
  const [loading, setLoading] = useState(true);
  // Iter 418.j — default filter is "Open" not "All" (the most common
  // working view for warehouse staff — you don't want to sift through
  // closed + void slips every time you land on the hub).
  const [filter, setFilter] = useState("open");
  const [search, setSearch] = useState("");
  // 2026-02-18 Round E — outstanding-by-supplier view + CSV export.
  const [outstandingView, setOutstandingView] = useState(false);
  const [outstandingData, setOutstandingData] = useState(null);
  const [outstandingBusy, setOutstandingBusy] = useState(false);
  // Iter 382 — "New picking slip" modal.
  const [newOpen, setNewOpen] = useState(false);
  // Iter 420 — deep-link from a Project's Linked tab. When URL is
  // /picking?new=1&project_id=xxx we auto-open the wizard AND stamp
  // project_id on the created list so it links back automatically.
  const [searchParams] = useSearchParams();
  const preselectProjectId = searchParams.get("project_id") || null;
  const autoOpenNew = searchParams.get("new") === "1";
  useEffect(() => {
    if (autoOpenNew) setNewOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenNew]);


  const loadOutstanding = async () => {
    setOutstandingBusy(true);
    try {
      const r = await api.get(`/picking/outstanding`);
      setOutstandingData(r.data);
    } catch (err) {
      toast.error("Failed to load outstanding items.");
    } finally {
      setOutstandingBusy(false);
    }
  };

  const exportOutstandingCsv = async () => {
    try {
      const r = await api.get(`/picking/outstanding`, {
        params: { fmt: "csv" },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `picking_outstanding_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV download started.");
    } catch (err) {
      toast.error("CSV export failed.");
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      // Pull all picking lists for the tenant (project_id absent → all).
      // Iter 418.j — request voided + archived lists too so the new
      // Void filter tab has data to render. Backend derives the phase
      // and the frontend filters client-side, which lets a single
      // fetch drive every tab without a status round-trip.
      const r = await api.get(`/picking/lists`, { params: { limit: 500, include_voided: true } });
      const rows = r.data?.items || [];
      setLists(rows);
      // Iter 418.bn (Dirk, 2026-02-28) — Perf fix. Backend now inlines
      // `_project` / `_customer` / `_jobcards` / `_sales_docs` on each
      // list row so we can build the label maps from ONE response —
      // no more N+1 GET per project/customer/JC/SO. Cuts the "why is
      // picking so slow?" 5–10 s open time to sub-second.
      const projMap = {};
      const custMap = {};
      const jcMap = {};
      const sdMap = {};
      for (const l of rows) {
        if (l._project) projMap[l._project.id] = { id: l._project.id, title: l._project.title, short_id: l._project.short_id };
        if (l._customer) custMap[l._customer.id] = { id: l._customer.id, ref: l._customer.account_no || l._customer.short_id || "", name: l._customer.name };
        for (const jc of (l._jobcards || [])) jcMap[jc.id] = { id: jc.id, title: jc.title, short_id: jc.short_id };
        for (const sd of (l._sales_docs || [])) sdMap[sd.id] = { id: sd.id, document_number: sd.document_number, category: sd.category };
      }
      setProjects(projMap);
      setCustomers(custMap);
      setJobcards(jcMap);
      setSalesDocs(sdMap);
    } catch (err) {
      setLists([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return lists.filter((l) => {
      // Iter 398f — filter on the derived `phase` field (Open /
      // Dispatched / Returned / Closed) instead of the legacy `status`.
      // The 'all' bucket keeps everything.
      if (filter !== "all" && (l.phase || "open") !== filter) return false;
      if (!q) return true;
      const proj = projects[l.project_id];
      const cust = customers[l.customer_id];
      const displayName = displayPickingListName(l, jobcards);
      const hay = `${displayName} ${l.name || ""} ${proj?.title || ""} ${proj?.short_id || ""} ${cust?.name || ""} ${cust?.ref || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [lists, projects, customers, jobcards, filter, search]);

  // Iter 290zn — group by project_id when present, else by customer_id,
  // else "Standalone". This kills the literal "NULL" header that
  // surfaced for loose lists.
  const grouped = useMemo(() => {
    const g = {};
    filtered.forEach((l) => {
      let key;
      if (l.project_id)       key = `project:${l.project_id}`;
      else if (l.customer_id) key = `customer:${l.customer_id}`;
      else                    key = "standalone";
      (g[key] = g[key] || []).push(l);
    });
    return g;
  }, [filtered]);

  // Header label per group key.
  const groupHeader = (key) => {
    if (key === "standalone") {
      return { label: "Standalone (no project, no customer)", to: null, testid: "picking-hub-group-standalone" };
    }
    if (key.startsWith("project:")) {
      const pid = key.slice("project:".length);
      const proj = projects[pid] || {};
      const text = `${proj.short_id ? `${proj.short_id} · ` : ""}${proj.title || pid.slice(0, 8)}`;
      return { label: text, to: `/projects/${pid}`, testid: `picking-hub-project-${pid}` };
    }
    if (key.startsWith("customer:")) {
      const cid = key.slice("customer:".length);
      const cust = customers[cid] || {};
      const text = `Loose · ${cust.ref ? `${cust.ref} · ` : ""}${cust.name || cid.slice(0, 8)}`;
      return { label: text, to: `/customers/${cid}`, testid: `picking-hub-customer-${cid}` };
    }
    return { label: key, to: null, testid: "picking-hub-group-unknown" };
  };

  const totalOutstanding = useMemo(
    // Iter 398f — "still need attention" now excludes Closed AND
    // Returned (both terminal from a warehouse perspective).
    () => filtered.reduce((s, l) => s + (["open", "dispatched"].includes(l.phase) ? 1 : 0), 0),
    [filtered],
  );

  // Iter 398f — bucket counts drive the tab-badge numbers.
  const phaseCounts = useMemo(() => {
    const c = { all: lists.length, open: 0, dispatched: 0, returned: 0, closed: 0 };
    lists.forEach((l) => { c[l.phase || "open"] = (c[l.phase || "open"] || 0) + 1; });
    return c;
  }, [lists]);

  return (
    <div className="px-4 sm:px-6 py-5 max-w-5xl mx-auto" data-testid="picking-hub">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div className="flex items-center gap-3 min-w-0">
          <PackageCheck className="w-6 h-6 text-[#1FB6C1] shrink-0" />
          <div className="min-w-0">
            <h1 className="font-display text-2xl font-bold tracking-tight">Picking</h1>
            <p className="text-xs text-[#647087]">
              Every picking list across all projects · {filtered.length} list{filtered.length === 1 ? "" : "s"}
              {totalOutstanding > 0 && (
                <span className="ml-1 text-[#F59E0B]">· {totalOutstanding} still need attention</span>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <button
            type="button"
            onClick={() => {
              const next = !outstandingView;
              setOutstandingView(next);
              if (next && !outstandingData) loadOutstanding();
            }}
            className={`hv-btn text-xs ${outstandingView ? "hv-btn-primary" : "hv-btn-ghost"}`}
            data-testid="picking-hub-outstanding-toggle"
            title="Show outstanding items grouped by supplier"
          >
            <Building2 className="w-3 h-3" />
            {outstandingView ? "Hide outstanding" : "Outstanding by supplier"}
          </button>
          <button
            type="button"
            onClick={exportOutstandingCsv}
            className="hv-btn hv-btn-ghost text-xs"
            data-testid="picking-hub-export-csv"
            title="Download outstanding items as CSV (grouped by supplier)"
          >
            <Download className="w-3 h-3" /> CSV
          </button>
          <button
            type="button"
            onClick={() => setNewOpen(true)}
            className="hv-btn hv-btn-primary text-xs"
            data-testid="picking-hub-new"
          >
            <Plus className="w-3 h-3" /> New picking slip
          </button>
          <button
            type="button"
            onClick={load}
            className="hv-btn hv-btn-ghost text-xs"
            data-testid="picking-hub-refresh"
          >
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
        </div>
      </div>

      {/* Filter bar */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="flex gap-1 overflow-x-auto pb-1 -mx-1 px-1 max-w-full">
          {PHASE_FILTERS.map((f) => (
            <button
              key={f.v}
              type="button"
              onClick={() => setFilter(f.v)}
              className={`shrink-0 px-3 py-1 rounded-full text-[11px] border ${
                filter === f.v
                  ? "bg-[#1FB6C1]/20 border-[#1FB6C1] text-white"
                  : "bg-white/[0.02] border-white/5 text-[#a0aabf] hover:bg-white/[0.04]"
              }`}
              data-testid={`picking-hub-filter-${f.v}`}
            >
              {f.l}
              <span className="ml-1 font-mono text-[9px] opacity-70">
                {phaseCounts[f.v] || 0}
              </span>
            </button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-[#647087]" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by list name or project…"
            className="hv-input w-full pl-7 text-sm"
            data-testid="picking-hub-search"
          />
        </div>
      </div>

      {/* Body */}
      {loading && (
        <div className="text-center py-12 text-[#647087]">
          <Loader2 className="w-4 h-4 inline animate-spin" /> Loading picking lists…
        </div>
      )}

      {/* 2026-02-18 Round E — outstanding-by-supplier inline view.
          Replaces the lists grid when toggled on. */}
      {!loading && outstandingView && (
        <OutstandingBySupplier
          data={outstandingData}
          busy={outstandingBusy}
          onRefresh={loadOutstanding}
        />
      )}

      {!loading && !outstandingView && filtered.length === 0 && (
        <div className="text-center py-12 text-[#647087]">
          <PackageCheck className="w-10 h-10 mx-auto mb-2 opacity-30" />
          <div className="text-sm">No picking lists match the current filter.</div>
          <div className="text-[11px] mt-1">
            Open a project and tap &quot;New list&quot; in the Picking card to create one.
          </div>
        </div>
      )}

      {!loading && !outstandingView && filtered.length > 0 && (
        <div className="space-y-4" data-testid="picking-hub-lists">
          {Object.entries(grouped).map(([key, projLists]) => {
            const header = groupHeader(key);
            return (
              <div key={key} className="hv-card p-3 sm:p-4">
                {header.to ? (
                  <Link
                    to={header.to}
                    className="text-[10px] uppercase tracking-wider text-[#1FB6C1] font-bold mb-2 inline-block hover:underline"
                    data-testid={header.testid}
                  >
                    {header.label}
                  </Link>
                ) : (
                  <div
                    className="text-[10px] uppercase tracking-wider text-[#647087] font-bold mb-2"
                    data-testid={header.testid}
                  >
                    {header.label}
                  </div>
                )}
                <div className="space-y-1.5">
                  {projLists.map((l) => {
                    const phase = l.phase || "open";
                    const pill = PHASE_PILL[phase] || PHASE_PILL.open;
                    const cust = customers[l.customer_id];
                    const linkedJcs = (l.jobcard_ids || []).map((jid) => jobcards[jid]).filter(Boolean);
                    const linkedSds = (l.source_sales_doc_ids || []).map((sid) => salesDocs[sid]).filter(Boolean);
                    const totals = l.totals || {};
                    return (
                      <Link
                        key={l.id}
                        to={`/picking/lists/${l.id}`}
                        className="block bg-white/[0.02] hover:bg-white/[0.05] border border-white/5 rounded-md p-2.5 transition-colors"
                        data-testid={`picking-hub-list-${l.id}`}
                      >
                        <div className="flex items-center gap-2 flex-wrap">
                          <span
                            className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded shrink-0"
                            style={{ background: `${pill.c}1f`, color: pill.c }}
                            data-testid={`picking-hub-phase-${phase}-${l.id}`}
                          >
                            {pill.l}
                          </span>
                          <div className="text-sm font-medium text-white truncate flex-1 min-w-0">
                            {displayPickingListName(l, jobcards)}
                          </div>
                          {l.due_date && (
                            <span className="text-[10px] text-[#F59E0B] flex items-center gap-1 shrink-0">
                              <AlertTriangle className="w-3 h-3" /> due {l.due_date}
                            </span>
                          )}
                        </div>
                        {/* Iter 398f — family-tree chip strip: customer,
                            job cards, and linked sales orders. Mirrors
                            the EntityTrail bar at the top so users can
                            spot orphans (no customer) at a glance. */}
                        {(cust || linkedJcs.length > 0 || linkedSds.length > 0) && (
                          <div className="flex items-center gap-1 flex-wrap mt-1">
                            {cust && (
                              <span
                                className="text-[10px] px-1.5 py-0.5 rounded border border-emerald-400/40 bg-emerald-400/5 text-emerald-300"
                                data-testid={`picking-hub-chip-customer-${l.id}`}
                              >
                                {cust.ref ? `${cust.ref} · ` : ""}{cust.name}
                              </span>
                            )}
                            {linkedJcs.slice(0, 2).map((jc) => (
                              <span
                                key={jc.id}
                                className="text-[10px] px-1.5 py-0.5 rounded border border-amber-500/40 bg-amber-500/5 text-amber-300"
                                data-testid={`picking-hub-chip-jc-${jc.id}`}
                              >
                                {jc.short_id || jc.id.slice(0, 6)}
                              </span>
                            ))}
                            {linkedJcs.length > 2 && (
                              <span className="text-[10px] text-[#647087]">
                                +{linkedJcs.length - 2} JCs
                              </span>
                            )}
                            {linkedSds.slice(0, 2).map((sd) => (
                              <span
                                key={sd.id}
                                className="text-[10px] px-1.5 py-0.5 rounded border border-cyan-500/40 bg-cyan-500/5 text-cyan-300"
                                data-testid={`picking-hub-chip-sd-${sd.id}`}
                              >
                                {sd.document_number || (sd.category || "doc").replace("_", " ")}
                              </span>
                            ))}
                            {linkedSds.length > 2 && (
                              <span className="text-[10px] text-[#647087]">
                                +{linkedSds.length - 2} docs
                              </span>
                            )}
                            {!cust && (
                              <span
                                className="text-[10px] px-1.5 py-0.5 rounded border border-rose-500/40 bg-rose-500/5 text-rose-300"
                                data-testid={`picking-hub-chip-orphan-${l.id}`}
                              >
                                orphan (no customer)
                              </span>
                            )}
                          </div>
                        )}
                        {/* Row totals — tiny mono strip so techs
                            can spot progress without opening the list. */}
                        {totals.rows > 0 && (
                          <div className="text-[10px] text-[#647087] mt-1 font-mono flex flex-wrap gap-x-2">
                            <span>{totals.rows} rows</span>
                            <span>req {totals.qty_required}</span>
                            <span className="text-[#A78BFA]">pick {totals.qty_picked}</span>
                            <span className="text-[#38BDF8]">disp {totals.qty_dispatched}</span>
                            <span className="text-[#F59E0B]">ret {totals.qty_returned}</span>
                          </div>
                        )}
                        <div className="text-[10px] text-[#647087] mt-0.5">
                          Created {l.created_at?.slice(0, 10)}
                          {l.created_by_name && <> · by {l.created_by_name}</>}
                          {l.closed_at && (
                            <> · closed {l.closed_at.slice(0, 10)}{l.closed_by_name ? ` by ${l.closed_by_name}` : ""}</>
                          )}
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {newOpen && (
        <NewPickingSlipModal
          open={newOpen}
          onClose={() => setNewOpen(false)}
          projectId={preselectProjectId}
          onCreated={({ list }) => {
            setNewOpen(false);
            load();
            if (list?.id) {
              window.location.href = `/picking/${list.id}`;
            }
          }}
        />
      )}
    </div>
  );
}


// 2026-02-18 Round E — Outstanding items grouped by supplier.
// Renders inline (no modal). Each supplier becomes a collapsible card
// with the SKU rows underneath, ordered by outstanding qty desc so the
// most urgent items appear first.
function OutstandingBySupplier({ data, busy, onRefresh }) {
  if (busy && !data) {
    return (
      <div className="text-center py-12 text-[#647087]">
        <Loader2 className="w-4 h-4 inline animate-spin" /> Aggregating outstanding items…
      </div>
    );
  }
  if (!data) {
    return (
      <div className="text-center py-12 text-[#647087]">
        <PackageCheck className="w-10 h-10 mx-auto mb-2 opacity-30" />
        <div className="text-sm">Tap refresh to pull the outstanding list.</div>
        <button
          type="button"
          onClick={onRefresh}
          className="hv-btn hv-btn-ghost text-xs mt-2"
          data-testid="picking-outstanding-load"
        >
          <RefreshCw className="w-3 h-3" /> Load
        </button>
      </div>
    );
  }
  const groups = (data.groups || []).slice().sort(
    (a, b) => b.total_outstanding - a.total_outstanding,
  );
  if (groups.length === 0) {
    return (
      <div className="text-center py-12 text-[#647087]" data-testid="picking-outstanding-empty">
        <PackageCheck className="w-10 h-10 mx-auto mb-2 opacity-30" />
        <div className="text-sm">Nothing outstanding — every picking row is fulfilled.</div>
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="picking-outstanding-list">
      <div className="text-[10px] uppercase tracking-wider text-[#647087]">
        Outstanding · {data.total_rows} row{data.total_rows === 1 ? "" : "s"} across {data.total_groups} supplier{data.total_groups === 1 ? "" : "s"}
      </div>
      {groups.map((g) => (
        <div
          key={g.supplier_id}
          className="hv-card p-3"
          data-testid={`picking-outstanding-supplier-${g.supplier_id}`}
        >
          <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
            <div className="flex items-center gap-2 min-w-0">
              <Building2 className="w-4 h-4 text-[#1FB6C1] shrink-0" />
              <div className="text-sm font-medium text-white truncate">{g.supplier_name}</div>
              <span className="text-[10px] text-[#647087] font-mono">
                {g.rows.length} row{g.rows.length === 1 ? "" : "s"}
              </span>
            </div>
            <div className="text-[10px] text-[#F59E0B] font-mono">
              total outstanding · {g.total_outstanding.toFixed(2)}
            </div>
          </div>
          <div className="border border-white/5 rounded overflow-x-auto">
            <table className="w-full text-[11px] min-w-[480px]">
              <thead className="bg-white/[0.04] text-[9px] uppercase tracking-wider text-[#647087]">
                <tr>
                  <th className="text-left p-1.5">SKU</th>
                  <th className="text-left p-1.5">Name</th>
                  <th className="text-right p-1.5">Outstanding</th>
                  <th className="text-right p-1.5">Req</th>
                  <th className="text-left p-1.5">Source</th>
                  <th className="text-left p-1.5">Project</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {g.rows
                  .slice()
                  .sort((a, b) => b.qty_outstanding - a.qty_outstanding)
                  .map((r) => (
                    <tr key={r.item_id} data-testid={`picking-outstanding-row-${r.item_id}`}>
                      <td className="p-1.5 font-mono text-white/80">{r.sku || "—"}</td>
                      <td className="p-1.5 text-white truncate max-w-[260px]">{r.name}</td>
                      <td className="p-1.5 text-right font-mono text-[#F59E0B] font-bold">
                        {r.qty_outstanding} <span className="opacity-60 text-[9px]">{r.unit}</span>
                      </td>
                      <td className="p-1.5 text-right font-mono">{r.qty_required}</td>
                      <td className="p-1.5 text-[#a0aabf]">
                        {r.source_doc_number || r.source || "—"}
                      </td>
                      <td className="p-1.5">
                        <Link
                          to={`/picking/lists/${r.list_id}`}
                          className="text-[#1FB6C1] hover:underline flex items-center gap-0.5"
                          data-testid={`picking-outstanding-link-${r.item_id}`}
                        >
                          open <ChevronRightIcon className="w-2.5 h-2.5" />
                        </Link>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}
